/**
 * ClimateService - Handles climate data and overlays
 */

export class ClimateService {
  constructor() {
    this.climateData = null;
    this.temperatureData = new Map();
    this.precipitationData = new Map();
    this.seasonalData = new Map();
    this.dataLoaded = false;
  }

  /**
   * Load climate data
   */
  async loadClimateData() {
    try {
      console.log('Loading climate data...');

      // Initialize climate zones and data
      this.initializeClimateZones();
      this.initializeTemperatureData();
      this.initializePrecipitationData();
      this.initializeSeasonalData();

      this.climateData = {
        loaded: true,
        version: '1.0.0',
        lastUpdated: new Date().toISOString(),
        source: 'Köppen Climate Classification Enhanced'
      };

      this.dataLoaded = true;
      console.log('Climate data loaded successfully');
    } catch (error) {
      throw new Error(`Failed to load climate data: ${error.message}`);
    }
  }

  /**
   * Initialize climate zones based on Köppen classification
   */
  initializeClimateZones() {
    this.climateZones = {
      // Tropical climates (A)
      'Af': { // Tropical rainforest
        description: 'High precipitation year-round, no dry season',
        avgTemp: [25, 28], // min, max in Celsius
        avgPrecipitation: [2000, 4000], // mm per year
        regions: [
          { latMin: -10, latMax: 10, lonMin: -80, lonMax: -45 }, // Amazon
          { latMin: -10, latMax: 10, lonMin: 10, lonMax: 30 },   // Congo
          { latMin: -10, latMax: 10, lonMin: 95, lonMax: 155 }   // Southeast Asia
        ]
      },
      'Am': { // Tropical monsoon
        description: 'Short dry season, heavy monsoon rains',
        avgTemp: [24, 30],
        avgPrecipitation: [1500, 2500],
        regions: [
          { latMin: 5, latMax: 25, lonMin: 70, lonMax: 105 }, // South Asia
          { latMin: -15, latMax: 5, lonMin: 110, lonMax: 140 } // Northern Australia
        ]
      },
      'Aw': { // Tropical savanna
        description: 'Distinct wet and dry seasons',
        avgTemp: [22, 32],
        avgPrecipitation: [800, 1600],
        regions: [
          { latMin: -20, latMax: 15, lonMin: -25, lonMax: 50 }, // African savanna
          { latMin: -25, latMax: -10, lonMin: -60, lonMax: -45 } // Cerrado
        ]
      },

      // Arid climates (B)
      'BWh': { // Hot desert
        description: 'Very low precipitation, high temperatures',
        avgTemp: [20, 40],
        avgPrecipitation: [0, 250],
        regions: [
          { latMin: 15, latMax: 35, lonMin: -20, lonMax: 50 }, // Sahara/Arabia
          { latMin: 20, latMax: 40, lonMin: -120, lonMax: -105 }, // Southwestern US
          { latMin: -30, latMax: -15, lonMin: 115, lonMax: 145 } // Australian desert
        ]
      },
      'BWk': { // Cold desert
        description: 'Low precipitation, cold winters',
        avgTemp: [-5, 25],
        avgPrecipitation: [100, 300],
        regions: [
          { latMin: 35, latMax: 50, lonMin: 75, lonMax: 110 }, // Central Asia
          { latMin: 35, latMax: 45, lonMin: -115, lonMax: -105 } // Great Basin
        ]
      },

      // Temperate climates (C)
      'Cfa': { // Humid subtropical
        description: 'Hot, humid summers; mild winters',
        avgTemp: [10, 27],
        avgPrecipitation: [800, 1500],
        regions: [
          { latMin: 25, latMax: 40, lonMin: -100, lonMax: -75 }, // Southeastern US
          { latMin: 25, latMax: 40, lonMin: 110, lonMax: 125 } // Eastern China
        ]
      },
      'Cfb': { // Oceanic
        description: 'Mild temperatures, precipitation year-round',
        avgTemp: [5, 22],
        avgPrecipitation: [600, 1200],
        regions: [
          { latMin: 40, latMax: 60, lonMin: -10, lonMax: 25 }, // Western Europe
          { latMin: 40, latMax: 50, lonMin: -125, lonMax: -120 } // Pacific Northwest
        ]
      },
      'Csa': { // Mediterranean
        description: 'Dry summers, mild wet winters',
        avgTemp: [8, 25],
        avgPrecipitation: [400, 800],
        regions: [
          { latMin: 30, latMax: 45, lonMin: -10, lonMax: 40 }, // Mediterranean basin
          { latMin: 30, latMax: 40, lonMin: -125, lonMax: -115 } // California
        ]
      },

      // Continental climates (D)
      'Dfa': { // Hot-summer humid continental
        description: 'Hot summers, cold winters, precipitation year-round',
        avgTemp: [-10, 25],
        avgPrecipitation: [500, 1000],
        regions: [
          { latMin: 35, latMax: 50, lonMin: -100, lonMax: -75 }, // Great Lakes region
          { latMin: 40, latMax: 55, lonMin: 20, lonMax: 50 } // Eastern Europe
        ]
      },
      'Dfb': { // Warm-summer humid continental
        description: 'Warm summers, very cold winters',
        avgTemp: [-15, 20],
        avgPrecipitation: [400, 800],
        regions: [
          { latMin: 45, latMax: 60, lonMin: -100, lonMax: -75 }, // Northern Great Plains
          { latMin: 50, latMax: 65, lonMin: 20, lonMax: 60 } // Northern Europe
        ]
      },

      // Polar climates (E)
      'ET': { // Tundra
        description: 'Permafrost, short cool summers',
        avgTemp: [-25, 10],
        avgPrecipitation: [150, 400],
        regions: [
          { latMin: 60, latMax: 75, lonMin: -170, lonMax: 170 } // Arctic tundra
        ]
      },
      'EF': { // Ice cap
        description: 'Permanent ice and snow cover',
        avgTemp: [-40, -10],
        avgPrecipitation: [50, 200],
        regions: [
          { latMin: 75, latMax: 90, lonMin: -180, lonMax: 180 }, // Arctic
          { latMin: -90, latMax: -60, lonMin: -180, lonMax: 180 } // Antarctica
        ]
      }
    };
  }

  /**
   * Initialize temperature data grid
   */
  initializeTemperatureData() {
    const gridSize = 5; // 5-degree resolution

    for (let lat = -90; lat < 90; lat += gridSize) {
      for (let lon = -180; lon < 180; lon += gridSize) {
        const key = `${lat}_${lon}`;
        const climate = this.getClimateZone(lat + gridSize/2, lon + gridSize/2);

        // Generate realistic temperature data based on climate zone
        const tempData = this.generateTemperatureData(lat + gridSize/2, climate);
        this.temperatureData.set(key, tempData);
      }
    }
  }

  /**
   * Initialize precipitation data grid
   */
  initializePrecipitationData() {
    const gridSize = 5;

    for (let lat = -90; lat < 90; lat += gridSize) {
      for (let lon = -180; lon < 180; lon += gridSize) {
        const key = `${lat}_${lon}`;
        const climate = this.getClimateZone(lat + gridSize/2, lon + gridSize/2);

        const precipData = this.generatePrecipitationData(lat + gridSize/2, climate);
        this.precipitationData.set(key, precipData);
      }
    }
  }

  /**
   * Initialize seasonal variation data
   */
  initializeSeasonalData() {
    const gridSize = 10; // Coarser resolution for seasonal data

    for (let lat = -90; lat < 90; lat += gridSize) {
      for (let lon = -180; lon < 180; lon += gridSize) {
        const key = `${lat}_${lon}`;
        const seasonalData = this.generateSeasonalData(lat + gridSize/2, lon + gridSize/2);
        this.seasonalData.set(key, seasonalData);
      }
    }
  }

  /**
   * Get climate zone for coordinates
   */
  getClimateZone(lat, lon) {
    for (const [code, data] of Object.entries(this.climateZones)) {
      for (const region of data.regions) {
        if (lat >= region.latMin && lat <= region.latMax &&
            lon >= region.lonMin && lon <= region.lonMax) {
          return code;
        }
      }
    }

    // Fallback classification
    const absLat = Math.abs(lat);
    if (absLat >= 66.5) return 'EF'; // Ice cap
    if (absLat >= 60) return 'ET';   // Tundra
    if (absLat >= 40) return 'Cfb';  // Oceanic
    if (absLat >= 23.5) return 'Csa'; // Mediterranean
    return 'Af'; // Tropical
  }

  /**
   * Generate realistic temperature data
   */
  generateTemperatureData(lat, climate) {
    const absLat = Math.abs(lat);
    let baseTemp = 30 - (absLat * 0.6); // Temperature decreases with latitude

    // Adjust based on climate zone
    const climateData = this.climateZones[climate];
    if (climateData) {
      const [minTemp, maxTemp] = climateData.avgTemp;
      baseTemp = (minTemp + maxTemp) / 2;
    }

    // Generate monthly temperatures
    const monthlyTemps = [];
    for (let month = 0; month < 12; month++) {
      // Add seasonal variation (Northern/Southern hemisphere)
      let seasonalAdjust = 0;
      if (lat > 0) { // Northern hemisphere
        seasonalAdjust = 10 * Math.cos((month - 6) * Math.PI / 6);
      } else { // Southern hemisphere
        seasonalAdjust = 10 * Math.cos(month * Math.PI / 6);
      }

      const temp = baseTemp + seasonalAdjust + (Math.random() - 0.5) * 4;
      monthlyTemps.push(Math.round(temp * 10) / 10);
    }

    return {
      annual: Math.round(baseTemp * 10) / 10,
      monthly: monthlyTemps,
      min: Math.min(...monthlyTemps),
      max: Math.max(...monthlyTemps),
      range: Math.max(...monthlyTemps) - Math.min(...monthlyTemps)
    };
  }

  /**
   * Generate realistic precipitation data
   */
  generatePrecipitationData(lat, climate) {
    let basePrecip = 1000; // Default 1000mm annually

    // Adjust based on climate zone
    const climateData = this.climateZones[climate];
    if (climateData) {
      const [minPrecip, maxPrecip] = climateData.avgPrecipitation;
      basePrecip = (minPrecip + maxPrecip) / 2;
    }

    // Generate monthly precipitation
    const monthlyPrecip = [];
    for (let month = 0; month < 12; month++) {
      let monthlyAmount = basePrecip / 12;

      // Add seasonal patterns based on climate
      if (climate.startsWith('A')) { // Tropical
        if (climate === 'Aw') {
          // Savanna: wet season adjustment
          monthlyAmount *= lat > 0 ?
            (month >= 4 && month <= 9 ? 2 : 0.3) :
            (month <= 3 || month >= 10 ? 2 : 0.3);
        }
      } else if (climate.startsWith('B')) { // Arid
        monthlyAmount *= 0.5 + Math.random() * 0.5; // Variable low amounts
      } else if (climate === 'Csa') { // Mediterranean
        monthlyAmount *= month >= 4 && month <= 9 ? 0.2 : 1.8; // Dry summer
      }

      monthlyPrecip.push(Math.round(monthlyAmount));
    }

    return {
      annual: Math.round(basePrecip),
      monthly: monthlyPrecip,
      wetSeason: this.identifyWetSeason(monthlyPrecip),
      drySeason: this.identifyDrySeason(monthlyPrecip)
    };
  }

  /**
   * Generate seasonal variation data
   */
  generateSeasonalData(lat, lon) {
    const seasons = {
      spring: { months: [2, 3, 4], hemisphere: lat >= 0 ? 'north' : 'south' },
      summer: { months: [5, 6, 7], hemisphere: lat >= 0 ? 'north' : 'south' },
      autumn: { months: [8, 9, 10], hemisphere: lat >= 0 ? 'north' : 'south' },
      winter: { months: [11, 0, 1], hemisphere: lat >= 0 ? 'north' : 'south' }
    };

    // Adjust for southern hemisphere
    if (lat < 0) {
      seasons.spring.months = [8, 9, 10];
      seasons.summer.months = [11, 0, 1];
      seasons.autumn.months = [2, 3, 4];
      seasons.winter.months = [5, 6, 7];
    }

    return {
      dominantSeason: this.getDominantSeason(lat),
      seasonalContrast: Math.abs(lat) > 40 ? 'high' : Math.abs(lat) > 20 ? 'moderate' : 'low',
      seasons
    };
  }

  /**
   * Get dominant season characteristics
   */
  getDominantSeason(lat) {
    const absLat = Math.abs(lat);

    if (absLat < 10) return 'wet_dry_tropical';
    if (absLat < 30) return 'subtropical_variation';
    if (absLat < 50) return 'four_seasons';
    if (absLat < 70) return 'short_summer_long_winter';
    return 'polar_night_day';
  }

  /**
   * Identify wet season months
   */
  identifyWetSeason(monthlyPrecip) {
    const avgPrecip = monthlyPrecip.reduce((a, b) => a + b, 0) / 12;
    const wetMonths = [];

    monthlyPrecip.forEach((precip, month) => {
      if (precip > avgPrecip * 1.2) {
        wetMonths.push(month);
      }
    });

    return wetMonths;
  }

  /**
   * Identify dry season months
   */
  identifyDrySeason(monthlyPrecip) {
    const avgPrecip = monthlyPrecip.reduce((a, b) => a + b, 0) / 12;
    const dryMonths = [];

    monthlyPrecip.forEach((precip, month) => {
      if (precip < avgPrecip * 0.5) {
        dryMonths.push(month);
      }
    });

    return dryMonths;
  }

  /**
   * Get climate data for coordinates
   */
  getClimateData(lat, lon, dataType = 'all') {
    const gridSize = 5;
    const gridLat = Math.floor(lat / gridSize) * gridSize;
    const gridLon = Math.floor(lon / gridSize) * gridSize;
    const key = `${gridLat}_${gridLon}`;

    const climate = this.getClimateZone(lat, lon);
    const result = {
      climate,
      description: this.climateZones[climate]?.description || 'Unknown climate',
      coordinates: { lat, lon }
    };

    if (dataType === 'temperature' || dataType === 'all') {
      result.temperature = this.temperatureData.get(key) || null;
    }

    if (dataType === 'precipitation' || dataType === 'all') {
      result.precipitation = this.precipitationData.get(key) || null;
    }

    if (dataType === 'seasonal' || dataType === 'all') {
      const seasonalKey = `${Math.floor(lat / 10) * 10}_${Math.floor(lon / 10) * 10}`;
      result.seasonal = this.seasonalData.get(seasonalKey) || null;
    }

    return result;
  }

  /**
   * Get climate overlay data for map visualization
   */
  getClimateOverlay(overlayType = 'temperature') {
    const overlayData = [];

    if (overlayType === 'temperature') {
      for (const [key, tempData] of this.temperatureData.entries()) {
        const [lat, lon] = key.split('_').map(Number);
        overlayData.push({
          lat: lat + 2.5, // Center of grid cell
          lon: lon + 2.5,
          value: tempData.annual,
          label: `${tempData.annual}°C`,
          color: this.getTemperatureColor(tempData.annual)
        });
      }
    } else if (overlayType === 'precipitation') {
      for (const [key, precipData] of this.precipitationData.entries()) {
        const [lat, lon] = key.split('_').map(Number);
        overlayData.push({
          lat: lat + 2.5,
          lon: lon + 2.5,
          value: precipData.annual,
          label: `${precipData.annual}mm`,
          color: this.getPrecipitationColor(precipData.annual)
        });
      }
    }

    return overlayData;
  }

  /**
   * Get color for temperature visualization
   */
  getTemperatureColor(temp) {
    if (temp <= -20) return '#000080'; // Dark blue (very cold)
    if (temp <= -10) return '#0066CC'; // Blue (cold)
    if (temp <= 0) return '#4da6ff';   // Light blue (cool)
    if (temp <= 10) return '#66ff66';  // Green (mild)
    if (temp <= 20) return '#ffff00';  // Yellow (warm)
    if (temp <= 30) return '#ff9900';  // Orange (hot)
    if (temp <= 40) return '#ff3300';  // Red (very hot)
    return '#800000'; // Dark red (extreme heat)
  }

  /**
   * Get color for precipitation visualization
   */
  getPrecipitationColor(precip) {
    if (precip <= 100) return '#8B4513';   // Brown (desert)
    if (precip <= 300) return '#DEB887';   // Tan (arid)
    if (precip <= 600) return '#F0E68C';   // Khaki (semi-arid)
    if (precip <= 1000) return '#90EE90';  // Light green (moderate)
    if (precip <= 1500) return '#32CD32';  // Lime green (humid)
    if (precip <= 2500) return '#228B22';  // Forest green (wet)
    if (precip <= 4000) return '#006400';  // Dark green (very wet)
    return '#003300'; // Very dark green (extremely wet)
  }

  /**
   * Get climate statistics for a region
   */
  getRegionClimateStats(bounds) {
    const stats = {
      climateZones: new Map(),
      avgTemperature: 0,
      avgPrecipitation: 0,
      temperatureRange: { min: Infinity, max: -Infinity },
      precipitationRange: { min: Infinity, max: -Infinity },
      sampleCount: 0
    };

    // Sample the region
    const sampleStep = 2;
    let tempSum = 0, precipSum = 0;

    for (let lat = bounds.south; lat <= bounds.north; lat += sampleStep) {
      for (let lon = bounds.west; lon <= bounds.east; lon += sampleStep) {
        const climateData = this.getClimateData(lat, lon);

        // Count climate zones
        const zone = climateData.climate;
        stats.climateZones.set(zone, (stats.climateZones.get(zone) || 0) + 1);

        // Accumulate temperature and precipitation
        if (climateData.temperature) {
          tempSum += climateData.temperature.annual;
          stats.temperatureRange.min = Math.min(stats.temperatureRange.min, climateData.temperature.min);
          stats.temperatureRange.max = Math.max(stats.temperatureRange.max, climateData.temperature.max);
        }

        if (climateData.precipitation) {
          precipSum += climateData.precipitation.annual;
          stats.precipitationRange.min = Math.min(stats.precipitationRange.min, climateData.precipitation.annual);
          stats.precipitationRange.max = Math.max(stats.precipitationRange.max, climateData.precipitation.annual);
        }

        stats.sampleCount++;
      }
    }

    if (stats.sampleCount > 0) {
      stats.avgTemperature = Math.round(tempSum / stats.sampleCount * 10) / 10;
      stats.avgPrecipitation = Math.round(precipSum / stats.sampleCount);
    }

    return stats;
  }

  /**
   * Check if service is ready
   */
  isReady() {
    return this.dataLoaded;
  }

  /**
   * Get month name
   */
  getMonthName(monthIndex) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex] || 'Unknown';
  }

  /**
   * Get service statistics
   */
  getStats() {
    return {
      dataLoaded: this.dataLoaded,
      climateZones: Object.keys(this.climateZones).length,
      temperatureGridSize: this.temperatureData.size,
      precipitationGridSize: this.precipitationData.size,
      seasonalGridSize: this.seasonalData.size
    };
  }
}
